'use client';

import React, { useEffect, useState } from 'react';
import {
  defaultLanguage,
  getLanguageFromPath,
  getLanguageFromValue,
  languages,
  withLanguagePrefix,
  type SiteLanguage,
} from '@/data/languages';

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const setLanguageCookie = (language: SiteLanguage) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `wto_lang=${language}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

const getInitialLanguage = (): SiteLanguage => {
  if (typeof window === 'undefined') return defaultLanguage;
  const fromPath = getLanguageFromPath(window.location.pathname);
  if (fromPath) return fromPath;
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (fromUrl) return getLanguageFromValue(fromUrl);
  return getLanguageFromValue(localStorage.getItem('wto_lang'));
};

const LanguageSelector: React.FC<{ compact?: boolean; theme?: 'dark' | 'light' }> = ({ compact = false, theme = 'dark' }) => {
  const [language, setLanguage] = useState<SiteLanguage>(defaultLanguage);

  useEffect(() => {
    const next = getInitialLanguage();
    setLanguage(next);
    localStorage.setItem('wto_lang', next);
    setLanguageCookie(next);
  }, []);

  const selected = languages.find((item) => item.code === language) ?? languages[0];

  const changeLanguage = (next: SiteLanguage) => {
    localStorage.setItem('wto_lang', next);
    setLanguageCookie(next);
    const url = new URL(window.location.href);
    url.pathname = withLanguagePrefix(url.pathname, next);
    url.searchParams.delete('lang');
    window.location.href = url.toString();
  };

  const color = theme === 'dark' ? '#FFFFFF' : '#0F2340';
  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(15,35,64,0.18)';
  const optionStyle = { backgroundColor: '#FFFFFF', color: '#0F2340' };

  return (
    <label className="relative inline-flex items-center gap-2">
      <span className="sr-only">Language</span>
      <img src={selected.flag} alt="" className="h-4 w-6 rounded-sm object-cover shadow-sm" aria-hidden="true" />
      <select
        value={language}
        onChange={(event) => changeLanguage(getLanguageFromValue(event.target.value))}
        className={`appearance-none rounded-full border bg-transparent font-body font-semibold uppercase outline-none transition-colors ${compact ? 'py-2 pl-2 pr-7 text-sm' : 'py-1.5 pl-2 pr-7 text-xs'}`}
        style={{ color, borderColor }}
        aria-label="Language"
      >
        {languages.map((item) => (
          <option key={item.code} value={item.code} style={optionStyle}>
            {item.code.toUpperCase()}
          </option>
        ))}
      </select>
      <svg className="pointer-events-none absolute right-2 h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color }}>
        <path d="M6 9l6 6 6-6" />
      </svg>
    </label>
  );
};

export default LanguageSelector;
